import React, { Suspense } from 'react';

import { AreaChart } from 'ui';

import { SelectCustomizable } from '../../../../../components/atoms/legacyAtoms';
import type { DailyInteractionPoint } from '../../../../../types/tutorUsage';

import './_tutor-usage-chart.scss';

const CHART_HEIGHT = 250;
const MODE_COLORS: Record<string, string> = {
  ALL: '#FF7450',
  STUDY_MODE: '#A845AA',
  PRACTICE_MODE: '#2563AC',
  REVISION_MODE: '#D6A52D',
};

type ModeOption = {
  label: string;
  value: string;
};

type TutorUsageChartProps = {
  dailyInteractions: DailyInteractionPoint[];
  description: string;
  interactionsLabel: string;
  modeOptions: ModeOption[];
  onModeChange: (option: ModeOption | null) => void;
  selectedMode: ModeOption;
  title: string;
};

function TutorUsageChart({
  dailyInteractions,
  description,
  interactionsLabel,
  modeOptions,
  onModeChange,
  selectedMode,
  title,
}: Readonly<TutorUsageChartProps>): JSX.Element | null {
  const chartData = dailyInteractions.map((d) => ({
    name: d.name,
    [interactionsLabel]: d.interactions,
  }));

  if (chartData.length === 0) return null;

  const chartColor = MODE_COLORS[selectedMode.value] ?? MODE_COLORS.ALL;
  const chartSeries = [{ color: chartColor, dataKey: interactionsLabel }];
  const legendLabel = selectedMode.value === 'ALL' ? interactionsLabel : selectedMode.label;

  return (
    <div className="tutor-usage-chart">
      <div className="tutor-usage-chart__header">
        <div className="tutor-usage-chart__header-text">
          <h3 className="tutor-usage-chart__title">{title}</h3>
          <p className="tutor-usage-chart__description">{description}</p>
        </div>
        <div className="tutor-usage-chart__filter">
          <SelectCustomizable onChange={onModeChange} options={modeOptions} size="default" value={selectedMode} variant="default" />
        </div>
      </div>

      <div className="tutor-usage-chart__container">
        <Suspense fallback={null}>
          <AreaChart data={chartData} height={CHART_HEIGHT} hasXAxis hasYAxis lineType="monotone" series={chartSeries} strokeWidth={2} title={title} yDomain={[0, 10]} />
        </Suspense>
      </div>

      <ul className="tutor-usage-chart__legend">
        <li className="tutor-usage-chart__legend-item">
          <span className="tutor-usage-chart__legend-color" style={{ backgroundColor: chartColor }} />
          {legendLabel}
        </li>
      </ul>
    </div>
  );
}

export default TutorUsageChart;
